import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MobileTabPanel, { a11yProps } from './MobileTabPanel';
import useLocalStorage from '../../Util/custom-hooks/UseLocalStorage';

// Both layouts stack the Vyur over a panel and let the reader drag the boundary
// between them: the chat on desktop, the tabbed panel on mobile.
//
// Three floors, in priority order once the window is too short to honour them
// all. The Vyur's wins - the slide is the point of the page - so the pane is
// squeezed below the size it can be dragged to rather than the slide losing
// room. The pane's hard floor wins over that in turn, because a pane squeezed
// past its own controls is no longer a pane: it is the composer row on desktop
// and the tab bar on mobile. Mobile's floors are lower throughout; a phone has
// less to give.
//
// The handle comes out of the Vyur's share. Mobile's is taller because it is
// dragged with a thumb rather than a cursor.
const SPLITTER_HEIGHT = 8;
const MOBILE_SPLITTER_HEIGHT = 20;
const RESIZE_STEP = 24; // px per arrow key press
const CHAT_MIN_HEIGHT = 180;
const CHAT_HARD_MIN_HEIGHT = 56; // the composer row
const VYUR_MIN_HEIGHT = 240;
const CHAT_DEFAULT_FRACTION = 0.3; // the split the desktop layout shipped with
const CHAT_HEIGHT_KEY = 'chat-height';
const MOBILE_PANEL_MIN_HEIGHT = 160;
const MOBILE_PANEL_HARD_MIN_HEIGHT = 48; // the tab bar
const MOBILE_VYUR_MIN_HEIGHT = 160;
const MOBILE_PANEL_DEFAULT_FRACTION = 0.4; // the split the mobile layout shipped with
const MOBILE_PANEL_HEIGHT_KEY = 'mobile-panel-height';

// One resizable pane: the bottom half of a stack, sized in px and remembered
// across reloads. Stored in px rather than as a fraction so the pane keeps the
// size it was given when the window changes, instead of scaling with it.
function useResizablePane({
    storageKey,
    label,
    containerHeight,
    paneMin,
    paneHardMin,
    vyurMin,
    defaultFraction,
    splitterHeight
}) {
    const [storedHeight, setStoredHeight] = useLocalStorage(storageKey, null);
    // The live size while a drag is in flight. Held here rather than in storage
    // so one drag is one write, not one per pointer sample.
    const [dragHeight, setDragHeight] = useState(null);
    // Tears down an in-flight drag's listeners; see the unmount effect below.
    const endDragRef = useRef(null);

    // A drag outlives the pane if the reader navigates out of the room mid-drag
    // - a route change, or loading flipping true. Without this the listeners
    // stay bound to the window and keep writing heights for a pane that is no
    // longer on the page.
    useEffect(
        () => () => {
            if (endDragRef.current) {
                endDragRef.current();
            }
        },
        []
    );

    // Clamped at render, not on write: containerHeight moves with the window,
    // so a height that was legal when it was chosen may not be legal now. When
    // the two floors cannot both be served, maxHeight lands under paneMin and
    // minHeight follows it down, which is what squeezes the pane rather than
    // the Vyur - and keeps aria-valuemin from overtaking aria-valuemax.
    const maxHeight = Math.max(paneHardMin, containerHeight - vyurMin - splitterHeight);
    const minHeight = Math.min(paneMin, maxHeight);
    const clamp = (value) => Math.min(Math.max(value, minHeight), maxHeight);
    const height = clamp(
        dragHeight == null
            ? storedHeight == null
                ? containerHeight * defaultFraction
                : storedHeight
            : dragHeight
    );

    // Pointer events rather than mouse events: one path covers mouse, touch and
    // pen. Listeners go on the window so a fast drag that outruns the handle
    // still tracks, and are torn down when the pointer is released.
    const onPointerDown = (event) => {
        // Primary button only. A right-click here would otherwise start a drag
        // Chrome never ends: it opens its context menu and never sends the
        // matching pointerup, leaving the listeners bound and the pane
        // resizing under a button nobody is holding.
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();

        const startY = event.clientY;
        const startHeight = height;
        let latestHeight = startHeight;

        const onPointerMove = (moveEvent) => {
            latestHeight = clamp(startHeight - (moveEvent.clientY - startY));
            setDragHeight(latestHeight);
        };
        // Removing the listeners is all this does on unmount; the commit to
        // storage happens only on a real release.
        const stopListening = () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerRelease);
            window.removeEventListener('pointercancel', onPointerRelease);
            endDragRef.current = null;
        };
        function onPointerRelease() {
            stopListening();
            // Only a drag that actually moved the boundary is a choice. A bare
            // click - focusing the handle before using the arrow keys, or a
            // thumb catching the 20px mobile one - would otherwise commit the
            // current height and freeze a pane that until then scaled with the
            // window, with nothing on screen to say so or to undo it.
            if (latestHeight !== startHeight) {
                setStoredHeight(latestHeight);
            }
            setDragHeight(null);
        }

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerRelease);
        window.addEventListener('pointercancel', onPointerRelease);
        endDragRef.current = stopListening;
        // Marks the handle as pressed before the first move arrives.
        setDragHeight(startHeight);
    };

    const onKeyDown = (event) => {
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setStoredHeight(clamp(height + RESIZE_STEP));
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setStoredHeight(clamp(height - RESIZE_STEP));
        }
    };

    return {
        height,
        splitterProps: {
            role: 'separator',
            'aria-orientation': 'horizontal',
            'aria-label': label,
            'aria-valuenow': Math.round(height),
            'aria-valuemin': minHeight,
            'aria-valuemax': maxHeight,
            tabIndex: 0,
            onPointerDown,
            onKeyDown,
            resizing: dragHeight != null,
            splitterHeight
        }
    };
}

// The divider between the two panes, which is also the drag handle. Keyboard
// users get the same range in RESIZE_STEP increments. The short bar in the
// middle is the only cue that the divider is draggable at all, so it stays
// visible rather than appearing on hover - there is no hover on a phone.
function PaneSplitter({ resizing, splitterHeight, ...props }) {
    return (
        <Box
            {...props}
            sx={{
                flex: '0 0 auto',
                height: `${splitterHeight}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'row-resize',
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                touchAction: 'none',
                backgroundColor: resizing ? 'action.selected' : 'transparent',
                '&:hover': { backgroundColor: 'action.hover' },
                '&:focus-visible': {
                    backgroundColor: 'action.hover',
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px'
                }
            }}
        >
            <Box
                sx={{
                    width: 36,
                    height: 2,
                    borderRadius: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.26)'
                }}
            />
        </Box>
    );
}

// Shared page shell for the member and guest room views: header on top, then
// either the desktop three-column layout (left pane / Vyur + chat / slide
// drawer) or the mobile stack (Vyur over a tabbed panel). The pages supply
// the panel contents; the guest page simply passes read-only variants, so
// both views stay visually identical by construction.
export default function RoomLayout({
    loading, // when true, only the loading placeholder renders
    fullscreen, // when true, the Vyur takes over the whole page
    renderVyur, // ({ fullscreen }) => node; called for both normal and fullscreen modes
    vyurOverlay = null, // node layered over the Vyur box (e.g. the clicker fade-out)
    accentColor = null, // main-box border color when set (Presentation Mode cue)
    header, // node rendered across the top
    leftPane, // node for the desktop left column
    chat = null, // node below the Vyur on desktop; hidden entirely when null
    rightPane = null, // node for the desktop right column (slide drawer); hidden when null
    mobileTabs = [], // [{ label (unique), icon?, content }] for the mobile bottom panel
    // { label, key } - an outside "show this tab" signal, keyed on a counter so
    // the same tab can be asked for twice. Ignored when there is no such tab.
    activateMobileTab = null
}) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [contentHeight, setContentHeight] = useState(window.innerHeight - 62);
    const [activeTab, setActiveTab] = useState(null);

    useEffect(() => {
        function handleResize() {
            setContentHeight(window.innerHeight - 62);
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Keyed on the counter alone: the label is usually the same one twice
    // running, and it is the press that has to move the panel, not a change of
    // destination. An unknown label falls through to the same fallback a
    // removed tab does, below.
    const activateTabKey = activateMobileTab ? activateMobileTab.key : null;
    useEffect(() => {
        if (activateTabKey == null) return;
        setActiveTab(activateMobileTab.label);
    }, [activateTabKey]);

    // Both are declared every render - hooks cannot be called conditionally -
    // but only the one belonging to the current layout is rendered. They keep
    // separate keys: a height chosen on a phone says nothing about the one
    // wanted on a desktop.
    const chatPane = useResizablePane({
        storageKey: CHAT_HEIGHT_KEY,
        label: 'Resize chat area',
        containerHeight: contentHeight,
        paneMin: CHAT_MIN_HEIGHT,
        paneHardMin: CHAT_HARD_MIN_HEIGHT,
        vyurMin: VYUR_MIN_HEIGHT,
        defaultFraction: CHAT_DEFAULT_FRACTION,
        splitterHeight: SPLITTER_HEIGHT
    });
    const mobilePane = useResizablePane({
        storageKey: MOBILE_PANEL_HEIGHT_KEY,
        label: 'Resize panel area',
        containerHeight: contentHeight,
        paneMin: MOBILE_PANEL_MIN_HEIGHT,
        paneHardMin: MOBILE_PANEL_HARD_MIN_HEIGHT,
        vyurMin: MOBILE_VYUR_MIN_HEIGHT,
        defaultFraction: MOBILE_PANEL_DEFAULT_FRACTION,
        splitterHeight: MOBILE_SPLITTER_HEIGHT
    });

    // Preserve the selected tab by identity when settings add or remove tabs.
    // If it was removed, prefer People (which both room views always provide).
    const activeMobileTab = mobileTabs.some((tab) => tab.label === activeTab)
        ? activeTab
        : (mobileTabs.find((tab) => tab.label === 'People') || mobileTabs[0] || {}).label;

    return (
        <Container maxWidth={false} disableGutters style={{ height: '100%' }}>
            {loading ? (
                <div> LOADING .... </div>
            ) : fullscreen ? (
                <div>{renderVyur({ fullscreen: true })}</div>
            ) : (
                <>
                    {header}
                    {isSmallScreen ? (
                        /* Mobile/Small Screen Layout - Vyur on top, tabbed panel at bottom */
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: `${contentHeight}px`,
                                maxHeight: `${contentHeight}px`
                            }}
                        >
                            {/* Top section - Vyur. minHeight 0 so it yields to
                                the panel as the splitter is dragged up. */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'hidden',
                                    p: 1,
                                    border: '3px solid',
                                    borderColor: accentColor || 'transparent'
                                }}
                            >
                                {renderVyur({ fullscreen: false })}
                                {vyurOverlay}
                            </Box>

                            {/* The divider under the Vyur, now draggable: it
                                resizes the whole tabbed panel, whichever tab
                                happens to be showing. */}
                            <PaneSplitter {...mobilePane.splitterProps} />

                            {/* Bottom section - Tabbed panel */}
                            <Box
                                sx={{
                                    flex: '0 0 auto',
                                    height: `${mobilePane.height}px`,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                {mobileTabs.length > 0 && (
                                    <>
                                        <Tabs
                                            value={activeMobileTab}
                                            onChange={(_, newValue) => setActiveTab(newValue)}
                                            variant="fullWidth"
                                            sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}
                                        >
                                            {mobileTabs.map((t, i) =>
                                                t.icon ? (
                                                    <Tab
                                                        key={t.label}
                                                        value={t.label}
                                                        icon={t.icon}
                                                        aria-label={t.label}
                                                        {...a11yProps(t.label)}
                                                    />
                                                ) : (
                                                    <Tab key={t.label} value={t.label} label={t.label} {...a11yProps(t.label)} />
                                                )
                                            )}
                                        </Tabs>
                                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                            {mobileTabs.map((t, i) => (
                                                <MobileTabPanel key={t.label} value={activeMobileTab} index={t.label}>
                                                    {t.content}
                                                </MobileTabPanel>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        /* Desktop Layout - Three column layout */
                        <Box
                            sx={{
                                display: 'flex',
                                height: `${contentHeight}px`,
                                maxHeight: `${contentHeight}px`
                            }}
                        >
                            {/* Left pane */}
                            <Box
                                sx={{
                                    width: '20%',
                                    minWidth: '200px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflowY: 'auto',
                                    borderRight: '1px solid rgba(0, 0, 0, 0.12)'
                                }}
                            >
                                {leftPane}
                            </Box>

                            {/* Middle section - vertical layout with Vyur and Chat */}
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden'
                                }}
                            >
                                {/* Top content area - Vyur. minHeight 0 so it
                                    yields to the chat as the splitter is
                                    dragged up; without it a flex item refuses
                                    to shrink past its content. */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: 'hidden',
                                        p: 2,
                                        border: '3px solid',
                                        borderColor: accentColor || 'transparent'
                                    }}
                                >
                                    {renderVyur({ fullscreen: false })}
                                    {vyurOverlay}
                                </Box>

                                {/* Bottom area - Chat (hidden when chat is turned off) */}
                                {chat && (
                                    <>
                                        {/* The divider that used to sit on top
                                            of the chat, now draggable. */}
                                        <PaneSplitter {...chatPane.splitterProps} />
                                        <Box
                                            sx={{
                                                flex: '0 0 auto',
                                                height: `${chatPane.height}px`,
                                                minHeight: 0,
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {chat}
                                        </Box>
                                    </>
                                )}
                            </Box>

                            {/* Right pane - Image Q (hidden when the Image Q is turned off) */}
                            {rightPane && (
                                <Box
                                    sx={{
                                        width: '20%',
                                        minWidth: '200px',
                                        overflowY: 'auto',
                                        borderLeft: '1px solid rgba(0, 0, 0, 0.12)'
                                    }}
                                >
                                    {rightPane}
                                </Box>
                            )}
                        </Box>
                    )}
                </>
            )}
        </Container>
    );
}
